import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import useToast from 'hooks/useToast';
// dayjs
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
// mui components
import { Grid, Typography, SxProps } from '@mui/material';
// components
import OutlinedInputController from 'controllers/outlined-input-controller';
import DatePickerController from 'controllers/date-picker-controller';
import PredecessorsCard from '../components/predecessor-card';
import SelectController from 'controllers/select-controller';
// helpers
import {
  utcDateToZoned,
  handleCalculateEndDate,
  calculateBusinessDays,
  toIsoUtcDate,
  zonedDateToUtc,
} from 'utils/postDateHelper';
/// types
import { PhaseStatusesEnum, TScheduleItemOptions } from 'redux-store/types';
import { IListSectionProps, IPredecessor } from '../types';
// api
import { handleChangedFromDatePicker } from 'redux-store/slices/manage-gantt-slice';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { LagTypeEnum } from 'redux-store/api/company';

// styles
import { listSelectionStyles } from './styles';
import { getMinStartDate } from './hepers';
import { AddPredecessorButton } from './add-predecessor-button';

dayjs.extend(utc);
dayjs.extend(timezone);

const ListSection: FC<IListSectionProps> = ({
  phasesOptions,
  startDate,
  endDate,
  shouldDisableDate,
  currentPhase,
  currentStartDate,
  currentWorkDays,
  workingDays,
  itemsList,
  isEdit,
  containerAdditionalStyles = {},
  isItemCompleted,
  lagType,
  project,
}) => {
  const [itemOptions, setItemOptions] = useState<TScheduleItemOptions>([]);

  const form = useFormContext();
  const showToast = useToast();
  const dispatch = useAppDispatch();

  const { fields, remove, append, update, replace } = useFieldArray({
    control: form.control,
    name: 'predecessors',
  });

  const itemStartDate = form.getValues('startDate');
  const linkedItems = form.getValues('linkedItems');

  const itemsListOptions = useMemo(() => {
    return itemsList.map((item) => {
      const formatedStartDate = dayjs.tz(item?.startDate, 'UTC').format('MM/DD');
      const formatedEndDate = dayjs.tz(item?.endDate, 'UTC').format('MM/DD');

      const hasInvalidStartDate =
        dayjs(itemStartDate).startOf('day').valueOf() <
        dayjs(item?.startDate).startOf('day').valueOf();

      const isItemDisabled = item?.status === PhaseStatusesEnum?.COMPLETED ? true : false;

      const isCyclicDependency = linkedItems?.some((linked) => linked?.itemId === item?.id);

      return {
        label: `${item?.title} (${formatedStartDate} - ${formatedEndDate})`,
        value: item?.id,
        disabled: hasInvalidStartDate || isItemDisabled || isCyclicDependency,
        disabledItemMessage: isItemDisabled
          ? 'Item already completed'
          : 'Predecessor start date must be before or equal to schedule item start date',
        endDate: item?.endDate,
        startDate: item?.startDate,
        workDays: item?.workDays,
      };
    });
  }, [itemsList, itemStartDate, linkedItems]);

  const filteredOptionsList = useMemo(() => {
    const removingIDs = new Set((fields as IPredecessor[])?.map((obj) => obj?.predecessorId));

    const filteredOptions = itemsListOptions?.filter((obj) => !removingIDs.has(obj?.value));

    return filteredOptions;
  }, [fields, itemsListOptions]);

  useEffect(() => {
    setItemOptions(filteredOptionsList);
  }, [filteredOptionsList]);

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
    };

    const numberInputs = document.querySelectorAll('input[type="number"]');

    numberInputs.forEach((input) => {
      input.addEventListener('wheel', handleWheel, { passive: false });
    });

    return () => {
      numberInputs.forEach((input) => {
        input.removeEventListener('wheel', handleWheel);
      });
    };
  }, []);

  const updatePredecessorsLagDays = (predecessors: IPredecessor[], changedStartDate: string) => {
    const newPredecessors = predecessors.map((pred) => {
      const isEndAfterOrEqual = pred.endDate >= changedStartDate;

      const rawDiff = isEndAfterOrEqual
        ? calculateBusinessDays(pred.startDate, changedStartDate, workingDays) - 1
        : calculateBusinessDays(pred.endDate, changedStartDate, workingDays) - 2;

      const diff = isEndAfterOrEqual ? rawDiff - pred.workDays : rawDiff;

      const lagDays =
        typeof pred?.lagDays === 'number' ? Math.min(pred.lagDays, diff) : pred?.lagDays;

      return {
        ...pred,
        workDaysDifference: diff,
        lagDays,
      };
    });

    replace(newPredecessors);
  };

  const changeLagDaysOnStartDateChange = (
    predecessors: IPredecessor[],
    changedStartDate: string
  ) => {
    if (lagType === LagTypeEnum.MAINTAIN) {
      updatePredecessorsLagDays(predecessors, changedStartDate);
    }
  };

  const workDaysChangeHandler = useCallback((e) => {
    const formStartDate = form.getValues('startDate');
    if (!!formStartDate) {
      form.setValue('workDays', e.target.value);

      const isoStart = toIsoUtcDate(formStartDate);

      const newEndDate = handleCalculateEndDate(isoStart, e.target.value, workingDays);

      form.setValue('endDate', newEndDate);
    } else {
      showToast({
        message: 'Before changing work days please add start date',
        type: 'error',
      });
    }
  }, []);

  const startDateChangeHandler = (e: any) => {
    const zonedStartDate = utcDateToZoned(e);

    const workDays = form.getValues('workDays') || currentWorkDays;

    if (currentStartDate) {
      const newEndDate = handleCalculateEndDate(e, workDays, workingDays);

      form.setValue('startDate', zonedStartDate);
      form.setValue('endDate', newEndDate);

      const formEndDate = form.getValues('endDate');
      if (formEndDate) {
        const endISO = toIsoUtcDate(formEndDate);
        const formWorkDays = calculateBusinessDays(e, endISO, workingDays);

        form.setValue('workDays', formWorkDays);
      }
    } else if (!currentStartDate && !!form.getValues('endDate')) {
      const newEndDate = handleCalculateEndDate(e, workDays, workingDays);

      form.setValue('startDate', zonedStartDate);
      form.setValue('endDate', newEndDate);

      const formEndDate = form.getValues('endDate');
      if (formEndDate) {
        const endISO = toIsoUtcDate(formEndDate);
        const formWorkDays = calculateBusinessDays(e, endISO, workingDays);
        form.setValue('workDays', formWorkDays);
      }
    } else if (!currentStartDate && !form.getValues('endDate') && !form.getValues('startDate')) {
      form.setValue('startDate', zonedStartDate);
      form.setValue('endDate', zonedStartDate);
      form.setValue('workDays', 1);
      form.clearErrors('endDate');
    } else {
      form.setValue('startDate', zonedStartDate);

      const formEndDate = form.getValues('endDate');
      if (formEndDate) {
        const endISO = toIsoUtcDate(formEndDate);
        const formWorkDays = calculateBusinessDays(e, endISO, workingDays);
        form.setValue('workDays', formWorkDays);
      }
    }

    const startDateState = form.getFieldState('startDate');

    if (startDateState?.error) {
      form.setError('startDate', null);
    }

    changeLagDaysOnStartDateChange(fields as IPredecessor[], e);
    dispatch(handleChangedFromDatePicker(true));
  };

  const endDateChangeHandler = useCallback((e) => {
    const end = utcDateToZoned(e);
    form.setValue('endDate', end);

    const formStartDate = form.getValues('startDate');

    if (!!formStartDate) {
      const startISO = toIsoUtcDate(formStartDate);
      const endISO = toIsoUtcDate(end);
      const formWorkDays = calculateBusinessDays(startISO, endISO, workingDays);
      form.setValue('workDays', formWorkDays);
    }

    if (!formStartDate) {
      form.setValue('startDate', end);
      form.setValue('workDays', 1);
    }

    const endDateState = form.getFieldState('endDate');

    if (!!endDateState?.error) {
      form.setError('endDate', null);
    }

    dispatch(handleChangedFromDatePicker(true));
  }, []);

  const defaultCalendarMonth = () => {
    if (!endDate && !!startDate) {
      return dayjs(startDate).toDate();
    }

    return null;
  };

  const minStartDate = useCallback(() => {
    return getMinStartDate(fields, itemsListOptions, lagType, workingDays);
  }, [fields, itemsListOptions, lagType, workingDays]);

  const containerStyles: SxProps = {
    ...(listSelectionStyles.content as SxProps),
    ...containerAdditionalStyles,
  };

  const onLagDaysInputChange = (
    value: number,
    currentPredecessor: IPredecessor,
    predecessors: IPredecessor[]
  ) => {
    if (!predecessors || predecessors.length === 0) {
      return;
    }

    const maxStartDate = predecessors.reduce((latest, p) =>
      new Date(p.startDate) > new Date(latest.startDate) ? p : latest
    ).startDate;

    const { startDate: predStartDate, workDays: predWorkDays } = currentPredecessor;

    const totalOffsetDays = value + predWorkDays + 1;

    const newZonedStartDate = handleCalculateEndDate(predStartDate, totalOffsetDays, workingDays);

    const newUtcStartDate = zonedDateToUtc(newZonedStartDate);

    const itemWorkDays = Number(form.getValues('workDays'));

    const newZonedEndDate = handleCalculateEndDate(newUtcStartDate, itemWorkDays, workingDays);

    if (maxStartDate > newUtcStartDate) {
      return;
    }

    form.setValue('startDate', newZonedStartDate);
    form.setValue('endDate', newZonedEndDate);

    updatePredecessorsLagDays(fields as IPredecessor[], newUtcStartDate);
  };

  const appendPredecessor = () => {
    const option = filteredOptionsList?.find((item) => !item.disabled);

    if (!option) return;

    const defaultPredecessor = {
      itemId: undefined,
      predecessorId: undefined,
      taskDependency: undefined,
      startDate: undefined,
      endDate: undefined,
      lagDays: undefined,
      workDays: undefined,
      workDaysDifference: undefined,
    };

    append(defaultPredecessor);
  };

  const isAddPredecessorDisabled = useMemo(() => {
    return filteredOptionsList.every(({ disabled }) => disabled);
  }, [filteredOptionsList]);

  return (
    <Grid sx={containerStyles}>
      <Grid sx={listSelectionStyles.container}>
        <Grid sx={listSelectionStyles.inputContainer}>
          <SelectController
            required
            isNative
            fullWidth
            withError
            isCapitalize
            name="phaseId"
            label="Select Phase"
            options={phasesOptions}
            defaultValue={currentPhase?.id}
            disabled={isItemCompleted}
          />
        </Grid>

        <Grid sx={listSelectionStyles.inputContainer}>
          <OutlinedInputController
            required
            fullWidth
            withError
            name="title"
            label="Item Title"
            placeholder="E.g. Electrical Rough-Ins"
            disabled={isItemCompleted}
          />

          <Grid sx={listSelectionStyles.datesInputContainer}>
            <Grid item sx={listSelectionStyles.datePickerContainer}>
              <DatePickerController
                shouldDisableDate={shouldDisableDate}
                icon
                required
                withError
                fullWidth
                label="Start Date"
                minDate={minStartDate()}
                {...form.register('startDate')}
                additionalOnChange={startDateChangeHandler}
                disabled={isItemCompleted}
              />
            </Grid>

            <Grid item sx={listSelectionStyles.dayContainer} className="dayContainer">
              <OutlinedInputController
                required
                fullWidth
                withError
                name="workDays"
                label="Work Days"
                type="number"
                defaultValue={currentWorkDays}
                additionalOnChange={workDaysChangeHandler}
                inputProps={{ min: 1 }}
                disabled={!form.getValues('startDate') || isItemCompleted}
              />
            </Grid>

            <Grid item sx={listSelectionStyles.datePickerContainer}>
              <DatePickerController
                shouldDisableDate={shouldDisableDate}
                required
                icon
                withError
                fullWidth
                name="endDate"
                label="End Date"
                minDate={!!startDate && dayjs.tz(dayjs(startDate), 'Greenwich')}
                additionalOnChange={endDateChangeHandler}
                defaultCalendarMonth={defaultCalendarMonth()}
                disabled={isItemCompleted}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {!(isItemCompleted && !fields.length) && (
        <Grid sx={listSelectionStyles.predecessorsContainer}>
          <Grid sx={listSelectionStyles.titleContainer}>
            <Typography sx={listSelectionStyles.predecessorsTitle}>Predecessors</Typography>
            {!!fields?.length && (
              <Typography>{lagType === LagTypeEnum.MINIMUM && 'Minimum'} Lag Days</Typography>
            )}
          </Grid>

          {(fields as IPredecessor[])?.map((field, index) => (
            <PredecessorsCard
              currentPhaseId={currentPhase?.id}
              project={project}
              onLagDaysInputChange={onLagDaysInputChange}
              updatePredecessorsLagDays={updatePredecessorsLagDays}
              field={field}
              fields={fields as IPredecessor[]}
              index={index}
              itemsListOptions={itemsListOptions}
              remove={remove}
              update={update}
              key={`${index}+${field?.lagDays}+${field.predecessorId}+${field.workDaysDifference}`}
              isEdit={isEdit}
              itemOptions={itemOptions}
              disabled={isItemCompleted}
              lagType={lagType}
              itemsList={itemsList}
              currentWorkDays={currentWorkDays}
            />
          ))}

          {!isItemCompleted && (
            <AddPredecessorButton
              onClick={appendPredecessor}
              isDisabled={isAddPredecessorDisabled}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default ListSection;
